
import {
  Component, PropSync, Prop, Watch, Mixins,
} from 'vue-property-decorator';
import { fetchSupportedServiceLevels } from '@/features/shipment/services/shipment.services';
import { CarrierFilterType } from '@/features/common/raw';
import { deepClone } from '@/features/common/helpers';
import FilterItem from './FilterItem.vue';
import focusMixin from '../../mixins/focusMixin';

export interface CarrierToFilter {
  name: string,
  show: boolean,
  levels: {
    name: string,
    value: string,
    show: boolean,
  }[],
}

export interface AvailableService {
  carrierCode: string,
  selectedService: string,
  serviceName: string,
}

@Component({
  components: {
    FilterItem,
  },
})
export default class FiltersModal extends Mixins(focusMixin) {
  @PropSync('show', { default: false }) showModalSync!: boolean;

  @PropSync('selectedCarriers', { required: true }) syncCarriers!: string[];

  @PropSync('selectedServiceLevels', {
    required: false,
    default: () => [],
  }) syncServiceLevel!: string[];

  @Prop({ required: false, default: true }) isServicesLevel!: boolean;

  @Prop({ default: '' }) className!: string;

  private carriersToFilter: CarrierToFilter[] = [];

  private lastCarriersToFilter: CarrierFilterType[] = [];

  get classes(): string {
    const classes = ['filters-modal', 'custom-scrollbar'];

    if (this.className) {
      classes.push(this.className);
    }

    return classes.join(' ');
  }

  get selectedCarriersCount(): number {
    return this.carriersToFilter
      .filter((item) => item.name !== 'TotalShip' && item.show === true).length;
  }

  get isSelectAllOptions(): boolean {
    const filterable = this.carriersToFilter.filter((item) => item.name !== 'TotalShip');
    return filterable.length > 0 && this.selectedCarriersCount === filterable.length;
  }

  set isSelectAllOptions(value: boolean) {
    this.carriersToFilter.forEach((el) => this.toggleService({ name: el.name, value }));
  }

  @Watch('showModalSync')
  private handleCloseModal(val) {
    if (!val) {
      this.carriersToFilter = deepClone(this.lastCarriersToFilter);
    }
  }

  async created(): Promise<void> {
    await this.populateSupportedServiceLevels();
    this.prepareFilter();
  }

  private async populateSupportedServiceLevels() {
    const levels = await fetchSupportedServiceLevels();

    const servicesWithLevels: CarrierFilterType[] = [];

    levels.forEach((level) => {
      const carrierCode = servicesWithLevels.find((s) => s.name === level.carrierCode);

      if (!carrierCode) {
        servicesWithLevels.push({
          name: level.carrierCode,
          show: true,
          levels: [],
        });
      }

      for (const service of servicesWithLevels) {
        if (service.name === level.carrierCode) {
          service.levels.push({
            name: level.displayName,
            value: level.selectedService,
            show: true,
          });
          break;
        }
      }
    });

    this.carriersToFilter = servicesWithLevels;
  }

  private prepareFilter() {
    const clonedCarrierFilterItems = [...this.carriersToFilter];

    for (let i = 0; i < clonedCarrierFilterItems.length; i += 1) {
      let isShow = !this.syncServiceLevel?.length;
      for (let j = 0; j < clonedCarrierFilterItems[i].levels.length; j += 1) {
        if (this.syncServiceLevel?.includes(
          clonedCarrierFilterItems[i].levels[j].value,
        )) {
          clonedCarrierFilterItems[i].levels[j].show = true;
          isShow = true;
        }
      }
      clonedCarrierFilterItems[i].show = isShow;
    }

    this.carriersToFilter = [...clonedCarrierFilterItems];
    this.updateLastCarriersToFilter();
  }

  private toggleService({ value, name }): void {
    const serviceToFilter = this.carriersToFilter.find((el) => el.name === name);
    if (serviceToFilter) {
      serviceToFilter.show = value;
      serviceToFilter.levels = serviceToFilter.levels.map((el) => {
        const clone = { ...el };
        clone.show = value;
        return clone;
      });
    }
  }

  private toggleLevel({ value, serviceName, levelName }): void {
    const serviceToFilter = this.carriersToFilter.find((el) => el.name === serviceName);
    const levelToFilter = serviceToFilter?.levels.find((el) => el.name === levelName);

    if (serviceToFilter && levelToFilter) {
      levelToFilter.show = value;
      const showService = serviceToFilter?.levels.find((el) => el.show);
      serviceToFilter.show = !!showService;
    }
  }

  private applyFilters(): void {
    this.updateLastCarriersToFilter();
    const filters = this.carriersToFilter.filter((service) => service.show === true);
    const carriers = filters.map((filterName) => filterName.name);
    this.syncCarriers = [...carriers];
    if (this.isServicesLevel) {
      this.syncServiceLevel = this.filterServiceLevel(filters);
    }
    this.$emit('applyFilters');
  }

  private filterServiceLevel = (carriers: CarrierToFilter[]): string[] => {
    const levels: string[] = [];

    carriers.forEach((carrier) => {
      carrier.levels.forEach((level) => {
        if (level.show) {
          levels.push(level.value);
        }
      });
    });

    return levels;
  }

  private updateLastCarriersToFilter(): void {
    this.lastCarriersToFilter = deepClone(this.carriersToFilter);
  }
}
